import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    CreateCustomField as CreateCustomFieldMutation,
    CreateCustomFieldVariables as CreateCustomFieldMutationVariables,
} from 'nutshell-graphql-types';

import CREATE_CUSTOM_FIELD from '../mutations/create-custom-field.graphql';

type Props = {
    children: (arg1: {
        onCreate: (queryVariables: CreateCustomFieldMutationVariables) => Promise<any>;
        loading: boolean;
        error: string | null | undefined;
        reset: () => void;
    }) => React.ReactElement;
};

export function CreateCustomFieldMutationWrapper(props: Props) {
    // Mimicking Apollo reset mutation https://www.apollographql.com/docs/react/data/mutations/#resetting-mutation-status
    // we are on 2.27.3 and it was introduced in 3.5.0+
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(undefined);

    // Apollo reset function would reset everything, but we only really need error reset
    function reset() {
        setErrorMessage(undefined);
    }

    const [createCustomField, {loading}] = useMutation<
        CreateCustomFieldMutation,
        CreateCustomFieldMutationVariables
    >(CREATE_CUSTOM_FIELD);

    return props.children({
        onCreate: (queryVariables: CreateCustomFieldMutationVariables) => {
            reset();

            return createCustomField({
                variables: {...queryVariables},
            })
                .then(
                    (mutationResults) =>
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.createCustomField &&
                        mutationResults.data.createCustomField.customField
                )
                .catch((error) => {
                    // Handle setting the error message
                    setErrorMessage(
                        (error &&
                            error.graphQLErrors &&
                            error.graphQLErrors.length &&
                            error.graphQLErrors[0].debugMessage &&
                            // When attribute already exists, that error message is actually helpful to show to user
                            error.graphQLErrors[0].debugMessage.includes('already exists')) ||
                            error.graphQLErrors[0].debugMessage.includes('plan limit')
                            ? `Error: ${error.graphQLErrors[0].debugMessage}`
                            : safelyGetGraphQLErrorMessage(error)
                    );

                    // When catching here, method above acts as a silent promise, so we need to reject with the error
                    return Promise.reject(error);
                });
        },
        loading,
        error: errorMessage,
        reset,
    });
}
